/**
 * GARDEN CLUB, OVER THE WIRE — the client's calls to the server's membership routes (server/index.js, "GARDEN
 * CLUB"). The entitlement itself, its strings and its selector are sim/subscription.ts; this file only moves
 * it. Same conventions as `startCheckout` in sim/payments.ts: same-origin POSTs carrying `authHeaders`, the
 * server's own error text surfaced as the thrown message.
 *
 * THE SERVER IS THE ONLY WRITER of a paid subscription. Nothing here sets one: checkout returns a Stripe URL
 * to go to, and coming back from it proves nothing — `fetchMembership` is how a page learns the webhook landed.
 */

import { authHeaders } from "./save";
import { track } from "./analytics";
import { coerceSubscription, type SubscriptionState, type SubscriptionStatus } from "./subscription";

/** The server's view of the player's membership. */
export interface Membership {
  /** Is Stripe configured on this server? False = the club cannot be joined here (local dev, or keys unset). */
  paymentsEnabled: boolean;
  /** Is a player identified (a Clerk session, or the anonymous id in a no-auth build)? */
  signedIn: boolean;
  /** The EFFECTIVE status, aged against the server clock (an `active` past its period end reads `lapsed`). */
  status: SubscriptionStatus;
  /** The stored state, for the dates and the cancel flag. */
  subscription: SubscriptionState;
  /** Is this a paid (Stripe-managed) membership — one with a billing page to manage? */
  managed: boolean;
  /** Garden Club prizes the player has earned and not yet collected (the save's `achClubHeld`). */
  waiting: number;
  /** Is JOINING paused? Kickstarter mode closes it with the Premium Shop (server /api/subscribe); members are untouched. */
  joinPaused: boolean;
}

/** What a page assumes before (or without) an answer: a visitor who is not a member. */
export const NO_MEMBERSHIP: Membership = {
  paymentsEnabled: false, signedIn: false, status: "none", subscription: { status: "none" }, managed: false, waiting: 0,
  joinPaused: false,
};

function coerceMembership(raw: unknown): Membership {
  if (!raw || typeof raw !== "object") return { ...NO_MEMBERSHIP };
  const o = raw as Record<string, unknown>;
  const status: SubscriptionStatus = o.status === "active" || o.status === "lapsed" ? o.status : "none";
  const subscription = coerceSubscription(o.subscription);
  return {
    paymentsEnabled: o.paymentsEnabled === true,
    signedIn: o.signedIn === true,
    status,
    subscription,
    managed: !!subscription.providerId,
    waiting: typeof o.waiting === "number" && o.waiting > 0 ? Math.floor(o.waiting) : 0,
    joinPaused: o.joinPaused === true,
  };
}

/** The player's membership. Never throws: an unreachable server reads as "not a member, can't join here". */
export async function fetchMembership(): Promise<Membership> {
  try {
    const res = await fetch("/api/subscription", { headers: await authHeaders(), cache: "no-store" });
    if (!res.ok) return { ...NO_MEMBERSHIP };
    return coerceMembership(await res.json());
  } catch {
    return { ...NO_MEMBERSHIP };
  }
}

async function post(path: string): Promise<Record<string, unknown>> {
  const res = await fetch(path, { method: "POST", headers: await authHeaders({ "content-type": "application/json" }), body: "{}" });
  const data = await res.json().catch(() => ({})) as Record<string, unknown>;
  if (!res.ok) throw new Error(typeof data.error === "string" ? data.error : `The request failed (${res.status}). Try again in a moment.`);
  return data;
}

/** Start a Garden Club Checkout; returns the Stripe URL to send the player to. */
export async function startSubscriptionCheckout(): Promise<string> {
  track("checkout_start", { product: "garden_club", kind: "club" });   // the end is the server's revenue_events
  const data = await post("/api/subscribe");
  if (typeof data.url !== "string") throw new Error("Checkout did not return a link. Try again in a moment.");
  return data.url;
}

/** Ask Stripe for the player's billing page (card, invoices); returns the URL to send them to. */
export async function openBillingPortal(): Promise<string> {
  const data = await post("/api/subscription/portal");
  if (typeof data.url !== "string") throw new Error("The billing page did not return a link. Try again in a moment.");
  return data.url;
}

/** Stop renewing at the end of the paid month. Returns the membership as it now stands. */
export async function cancelSubscription(): Promise<Membership> {
  const m = coerceMembership(await post("/api/subscription/cancel"));
  announceMembership(m.subscription);
  return m;
}

/** Undo a cancel before the month runs out. Returns the membership as it now stands. */
export async function resumeSubscription(): Promise<Membership> {
  const m = coerceMembership(await post("/api/subscription/resume"));
  announceMembership(m.subscription);
  return m;
}

/**
 * THE CHANNEL a site tab uses to tell an open GAME tab its membership changed — the same reason, and the same
 * mechanism, as PETALS_SPENT_CHANNEL in sim/payments.ts: the site and the game are separate routes, so the
 * only way to reach a running game is another tab. The game re-reads the server rather than trusting the
 * message's state (App.tsx), so a forged or stale message can at most cause one extra fetch.
 */
export const SUBSCRIPTION_CHANNEL = "shelf-garden:subscription";

export function announceMembership(sub: SubscriptionState): void {
  if (typeof BroadcastChannel === "undefined") return;
  const ch = new BroadcastChannel(SUBSCRIPTION_CHANNEL);
  ch.postMessage({ subscription: sub });
  ch.close();
}

/** Do two states differ in anything the player could see? (Used to skip no-op adoptions in the game.) */
export function sameSubscription(a: SubscriptionState, b: SubscriptionState): boolean {
  return a.status === b.status && a.periodEnd === b.periodEnd && a.providerId === b.providerId
    && !!a.cancelAtPeriodEnd === !!b.cancelAtPeriodEnd;
}
